import type { RouteObject } from 'react-router'
import { createBrowserRouter, createHashRouter } from 'react-router'

import ConditionRouter from '@web/app/routes/condition-router'
import configurationRoute from '@web/app/routes/configuration-router'
import { DeviceRouter } from '@web/app/routes/device-router'
import { lazyRoute } from '@web/app/routes/lazy-route'
import RequireRole from '@web/app/routes/require-role'
import SettingsRouter from '@web/app/routes/settings-router'
import { ErrorPage } from '@web/pages/error-page'
import { Spinner } from '@web/shared/components/spinner'
import { AppRoute, EquipmentRoute } from '@web/shared/constants/routes.constant'
import { EUserRole } from '@web/shared/enums/user/user-role.enum'

const conditionPageRoute = lazyRoute(
  () => import('@web/pages/condition/ui/condition'),
  ({ Condition }) => Condition
)

const plansConfiguratorRoute = lazyRoute(
  () => import('@web/widgets/plans-configurator/ui/plans-configurator'),
  ({ PlansConfigurator }) => PlansConfigurator
)

const configurationPageRoute = async () => {
  const { ConfigurationPage } = await import(
    '@web/pages/configuration/ui/configuration-page'
  )

  return {
    Component: () => (
      <RequireRole
        roles={[EUserRole.ADMIN, EUserRole.EXPERT, EUserRole.SPECIALIST]}
      >
        <ConfigurationPage />
      </RequireRole>
    )
  }
}

const devicesPageRoute = async () => {
  const { DevicesPage } = await import('@web/pages/device/ui/devices-page')

  return {
    Component: () => (
      <RequireRole
        roles={[EUserRole.ADMIN, EUserRole.EXPERT, EUserRole.SPECIALIST]}
      >
        <DevicesPage />
      </RequireRole>
    )
  }
}

const plansPageRoute = async () => {
  const { PlansPage } = await import('@web/pages/plans/ui/plans-page')

  return {
    Component: () => (
      <RequireRole
        roles={[EUserRole.ADMIN, EUserRole.EXPERT, EUserRole.SPECIALIST]}
      >
        <PlansPage />
      </RequireRole>
    )
  }
}

const router: RouteObject[] = [
  {
    path: '/',
    lazy: lazyRoute(
      () => import('@web/pages/home/ui/home-page'),
      ({ HomePage }) => HomePage
    ),
    errorElement: <ErrorPage />,
    HydrateFallback: Spinner
  },
  {
    lazy: lazyRoute(
      () => import('@web/pages/root/ui/root-page'),
      ({ RootPage }) => RootPage
    ),
    errorElement: <ErrorPage />,
    HydrateFallback: Spinner,
    children: [
      {
        path: AppRoute.Condition,
        lazy: conditionPageRoute,
        children: ConditionRouter
      },
      {
        path: AppRoute.Configuration,
        lazy: configurationPageRoute,
        children: configurationRoute
      },
      {
        path: AppRoute.Devices,
        lazy: devicesPageRoute,
        children: DeviceRouter
      },
      {
        path: AppRoute.Plans,
        lazy: plansPageRoute,
        children: [
          {
            path: EquipmentRoute.GroupId,
            lazy: plansConfiguratorRoute
          },
          {
            path: EquipmentRoute.MachineId,
            lazy: plansConfiguratorRoute
          },
          {
            path: EquipmentRoute.PointId,
            lazy: plansConfiguratorRoute
          },
          {
            path: EquipmentRoute.MeasurementId,
            lazy: plansConfiguratorRoute
          }
        ]
      },
      {
        path: AppRoute.Reports,
        lazy: lazyRoute(
          () => import('@web/pages/reports/ui/reports-page'),
          ({ ReportsPage }) => ReportsPage
        )
      },
      {
        path: AppRoute.Settings,
        lazy: lazyRoute(
          () => import('@web/pages/settings/ui/settings'),
          ({ Settings }) => Settings
        ),
        children: SettingsRouter
      },
      {
        path: AppRoute.Support,
        lazy: lazyRoute(
          () => import('@web/pages/support/ui/support-page'),
          ({ SupportPage }) => SupportPage
        )
      },
      {
        path: AppRoute.About,
        lazy: lazyRoute(
          () => import('@web/pages/about/ui/about-page'),
          ({ AboutPage }) => AboutPage
        )
      },
      {
        path: '*',
        lazy: lazyRoute(
          () => import('@web/pages/not-found/ui/not-found'),
          ({ NotFound }) => NotFound
        )
      }
    ]
  }
]

const RootRouter = window.isElectron
  ? createHashRouter(router)
  : createBrowserRouter(router)

export default RootRouter
